import {
	getTotalAIVehicleImageCount,
	getVDPDefaultVehicleImageAI,
	getVehicleDataForAttributes
} from '../../../utils/stellantis/utils';

const isMobile = () => {
	return window?.DDC?.dataLayer?.page?.attributes?.layoutType === 'mobile';
};

const getFormMapping = (mapping, trackingId) => {
	let { lead, type } = mapping[trackingId?.toUpperCase()] || {};

	if (typeof type === 'undefined') {
		const formMapping = mapping.OTHER;
		lead = formMapping.lead;
		type = formMapping.type;
	}

	return {
		formDescription: lead ? 'lead' : 'non-lead',
		formType: type
	};
};

const getFormAttrs = ({ isModal, mapping, trackingId, vehicle, vehicles }) => {
	const { formDescription, formType } = getFormMapping(
		mapping.form,
		trackingId
	);

	return {
		displayFormat: isModal ? 'modal' : 'inPage',
		displayType: isModal ? 'modal' : 'in-page',
		formDescription,
		formType,
		...getVehicleDataForAttributes({
			eventData: {
				vehicle
			},
			vehicles
		})
	};
};

const getDRLinkText = (element, widgetState) => {
	const finString = 'digitalretailing:build my deal:finance';
	const leaseString = 'digitalretailing:build my deal:lease';
	let conditionalString;

	switch (element) {
		case 'Credit Tier':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:credit score`;
			} else {
				conditionalString = `${leaseString}:credit score`;
			}
			break;
		case 'Payment Term':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:financing`;
			} else {
				conditionalString = `${leaseString}:term`;
			}
			break;
		case 'Incentives':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:specials`;
			} else {
				conditionalString = `${leaseString}:specials`;
			}
			break;
		case 'Tradein':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:add a trade in`;
			} else {
				conditionalString = `${leaseString}:add a trade in`;
			}
			break;
		case 'Down Payment':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:cash down`;
			} else {
				conditionalString = `${leaseString}:cash down`;
			}
			break;
		case 'Offer Price':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:send my offer`;
			} else {
				conditionalString = `${leaseString}:send my offer`;
			}
			break;
		case 'Continue To Sdp':
			if (widgetState === 'finance') {
				conditionalString = `${finString}:apply for credit`;
			} else {
				conditionalString = `${leaseString}:send my offer`;
			}
			break;
		case 'Lease Annual Miles':
			conditionalString = `${leaseString}:miles per year`;
			break;
		default:
		// do nothing
	}

	return conditionalString;
};

const formatUUID = (str) => {
	if (!str || str.length !== 32) {
		return str;
	}

	return `${str.slice(0, 8)}-${str.slice(8, 12)}-${str.slice(
		12,
		16
	)}-${str.slice(16, 20)}-${str.slice(20)}`;
};

export default {
	events: {
		'ddc.wstagging.first': [
			{
				payload: {
					digitalData: {
						page: {
							pageInfo: ({ flags } = {}) => ({
								dealerID:
									'[[dealerCode.cllc]] >> [[dealerCode.ar]] >> [[dealerCode.fiat]]',
								dealerName: '[[dealershipName]]',
								geoRegion: '[[country]]',
								language: '[[lookup.locale]]',
								pageName: `dealer:${window?.DDC?.dataLayer?.dealership?.country?.toLowerCase?.()}:[[fn.buildPageName]]`,
								providerName: 'DealerDotCom',
								responsiveState: '[[layoutType]]',
								siteTechnologyVersion: '[[variationId]]',
								tier3_dealerState: '[[stateProvince]]',
								tier3_dealerZipcode: '[[postalCode]]',
								tier3_trafficType: '',
								websiteTier: '[[tier]]',
								...(flags?.[
									'ws-tagging-stellantis-adobe-updates'
								] && {
									templateId: '[[pageAlias]]',
									totalAIVehicleImageCount:
										getTotalAIVehicleImageCount({
											vehicles:
												window?.DDC?.dataLayer?.vehicles
										})
								}),
								...(window?.DDC?.dataLayer?.page?.pageInfo
									?.isVdp && {
									vehicleMake: '[[make]]',
									vehicleModel: '[[model]]',
									vehicleStatus: '[[fn.getVehicleType]]',
									vehicleTrim: '[[trim]]',
									vehicleVin: '[[vin]]',
									vehicleYear: '[[modelYear]]',
									tier3_vehicleDisplayedPrice:
										'[[internetPrice]]',
									tier3_vehicleEngine: '[[engine]]',
									tier3_vehicleExteriorColor:
										'[[exteriorColor]]',
									tier3_vehicleInteriorColor:
										'[[interiorColor]]',
									tier3_vehicleMsrp: '[[internetPrice]]',
									tier3_vehicleStockNumber: '[[stockNumber]]',
									tier3_vehicleTransmission:
										'[[transmission]]',
									inventoryStatus: '[[fn.getInventoryStatus]]'
								})
							}),
							category: {
								pageType: '[[fn.buildPageType]]'
							}
						},
						events: [],
						version: '1.0',
						// probably the debug is another gap we need to fulfill
						debug: false,
						user: {
							authenticationState: null
						}
					}
				}
			}
		],
		'ddc.page.view': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'JSpageView',
					eventAction: 'contentView',
					attributes: ({ flags } = {}) => ({
						pageID: '[[window.digitalData.page.pageInfo.pageName]]_virtualPageView',
						...(window?.DDC?.dataLayer?.page?.pageInfo?.isVdp &&
							flags?.['ws-tagging-stellantis-adobe-updates'] && {
								VDPDefaultVehicleImageAI:
									getVDPDefaultVehicleImageAI({
										vehicles:
											window?.DDC?.dataLayer?.vehicles
									})
							})
					})
				}
			}
		],
		'ddc.page.vehicleDetailView': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'detailsView',
					eventAction: 'vehicle',
					attributes: {
						year: '[[year]]',
						make: '[[make]]',
						model: '[[model]]',
						trim: '[[trim]]',
						status: '[[fn.getVehicleType]]'
					}
				}
			}
		],
		'ddc.form.initiation': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'start',
					eventAction: 'form',
					attributes: ({
						eventData: { isModal, trackingId, vehicle = {} } = {},
						mapping
					} = {}) =>
						getFormAttrs({
							isModal,
							mapping,
							trackingId,
							vehicle,
							vehicles: window?.DDC?.dataLayer?.vehicles
						})
				}
			}
		],
		'ddc.form.confirmation': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'submit',
					eventAction: 'form',
					attributes: ({
						eventData: {
							fields,
							formEventId,
							isModal,
							submissionTimestamp,
							trackingId,
							vehicle = {}
						} = {},
						mapping
					} = {}) => ({
						...getFormAttrs({
							isModal,
							mapping,
							trackingId,
							vehicle,
							vehicles: window?.DDC?.dataLayer?.page?.pageInfo
								?.isConfirm
								? [vehicle]
								: window?.DDC?.dataLayer?.vehicles
						}),
						leadId: `${formatUUID(
							formEventId
						)}:${submissionTimestamp}`,
						hashedEmail: fields?.email
					})
				}
			}
		],
		'ddc.slideshow.click': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'carousel',
						assetName: '[[eventData.name]]',
						assetPosition: '[[eventData.position]]'
					}
				}
			}
		],
		widgetUserEvent: [
			{
				logic: {
					'eventData.widgetName': 'ws-site-text-search',
					'eventData.element': 'search query typed in',
					'eventData.action': 'impression'
				},
				payload: ({ eventData }) => ({
					type: 'CustomTagEvent',
					eventName: 'internal',
					eventAction: 'search',
					attributes: {
						searchTerm: eventData.fieldValue,
						type: 'other',
						searchResultsNumber: eventData.pageSearchResultsCount
					}
				})
			},
			{
				logic: {
					'eventData.action': 'clicked',
					'eventData.element': 'Save button',
					'eventData.result': 'Vehicle saved'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'save',
					eventAction: 'vehicle',
					attributes: '[[fn.getVehicleDataForAttributes]]'
				}
			},
			{
				logic: {
					'eventData.action': 'clicked',
					'eventData.result':
						'Email Signup for Price Alert Notification'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'priceAlert',
					eventAction: 'vehicle',
					attributes: '[[fn.getVehicleDataForAttributes]]'
				}
			},
			{
				logic: [
					// We want to only do specific DR events as not all of them are needed
					{
						widgetName: 'digital-retailing-cox',
						element: 'Credit Tier'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Payment Term'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Incentives'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Tradein'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Down Payment'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Offer Price'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Continue To Sdp'
					},
					{
						widgetName: 'digital-retailing-cox',
						element: 'Lease Annual Miles'
					}
				],
				payload: ({ eventData: { element, widgetState } }) => ({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction:
						element === 'Tradein' || element === 'Continue To Sdp'
							? 'buttonClick'
							: 'pageElementInteraction',
					attributes: {
						linkText: getDRLinkText(element, widgetState),
						isDigitalRetailing: true
					}
				})
			},
			{
				logic: {
					widgetName: 'ws-media-viewer',
					'eventData.result': 'Opens gallery modal'
				},
				payload: ({ eventData: { index } }) => ({
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: `in-page:gallery:${window.DDC.dataLayer.page.pageInfo.pageName
							.toLowerCase()
							.replace('sitebuilder_', '')
							.replace('_1', '')
							.replaceAll('_', '-')}-image-${index}-open`
					}
				})
			},
			{
				logic: {
					widgetName: 'ws-rec-vehicles',
					action: 'clicked'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText:
							'view featured inventory [[eventData.year]] [[eventData.make]] [[eventData.model]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'ws-model-selector',
					result: 'Model selector filtered'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'ws-model-selector',
					result: 'Navigated to model listings'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'ws-model-selector',
					result: 'Scrolled to'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: ({ eventData }) =>
							`browse new models ${
								eventData?.elementCTA?.includes?.('Next')
									? 'next'
									: 'previous'
							}`
					}
				}
			},
			{
				logic: {
					widgetName: 'model-selector',
					result: 'Model selector filtered'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				}
			},
			{
				logic: {
					widgetName: 'model-selector',
					result: 'Navigated to model listings'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'browse new models [[eventData.elementCTA]]'
					}
				}
			}
		],
		'ddc.link.offerClick': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'offerClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						offerId: '[[eventData.offerId]]',
						offerName: '[[eventData.offerName]]'
					}
				}
			}
		],
		'ddc.link.navClick': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.linkText]]',
						position: isMobile()
							? 'sidebar'
							: '[[eventData.position]]'
					}
				}
			}
		],
		'ddc.inventory.filterSearch': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'filtered',
					eventAction: 'search',
					attributes: {
						filters: '[[fn.buildFilteredSearchFilters]]',
						searchResultsNumber: '[[eventData.resultCount]]',
						type: '[[fn.getFilteredSearchType]]'
					}
				}
			}
		],
		'ddc.inventory.typedSearch': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'typed',
					eventAction: 'search',
					attributes: {
						searchTerm: '[[eventData.queryFacets.search]]',
						searchResultsNumber: '[[eventData.resultCount]]',
						type: '[[fn.getFilteredSearchType]]'
					}
				}
			}
		],
		'ddc.form.paymentCalcInit': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'start',
					eventAction: 'paymentCalculator',
					attributes: '[[fn.getVehicleDataForAttributes]]'
				}
			}
		],
		'ddc.link.generic': [
			// this block needs to be before the next one, as hamburgers usually have class 'btn'
			{
				logic: {
					isHamburgerMenu: true
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: 'nav open',
						position: 'sidebar'
					}
				}
			},
			{
				logic: ({
					eventData: {
						attributes = {},
						trackingSection = '',
						widgetName = ''
					} = {}
				}) =>
					(attributes?.class?.split?.(' ').includes('btn') ||
						trackingSection === 'Share' ||
						widgetName === 'ws-content-cta') &&
					!attributes?.class
						?.split?.(' ')
						.includes('facetmulti-freeform-search-submit'),
				payload: ({ eventData: { pageName, text } }) => {
					const payload = {
						type: 'CustomTagEvent',
						eventName: 'CTA',
						eventAction: 'buttonClick',
						attributes: {
							linkText: text
						}
					};

					if (pageName === 'SITEBUILDER_CUSTOM_FACTORY_ORDERS_1') {
						payload.attributes.linkText = `custom-factory-orders:${text?.toLowerCase?.()}`;
					} else if (
						pageName === 'SITEBUILDER_DODGE_POWER_BROKER_1'
					) {
						payload.attributes.linkText = `power-brokers:${text?.toLowerCase?.()}`;
					}

					return payload;
				},
				trigger: 'telephonicURL'
			},
			{
				logic: {
					'eventData.widgetName': 'ws-media-viewer',
					'eventData.attributes.class': 'pswp__button--arrow--right'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'modal:gallery:next-button'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'ws-media-viewer',
					'eventData.attributes.class': 'pswp__button--arrow--left'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'modal:gallery:prev-button'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'ws-media-viewer',
					'eventData.attributes.class': 'pswp__button--close'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: 'modal:gallery:close'
					}
				}
			},
			// this is for top level nav clicks off the main nav on desktop (but just in case position is dynamic)
			{
				logic: {
					'eventData.attributes.data-toggle': 'dropdown',
					trackingSection: 'Global Nav',
					location: 'mainNav'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.text]]',
						position: isMobile() ? 'sidebar' : 'topNav'
					}
				}
			},
			// and the following is for mobile, but just in case the position is dynamic
			{
				logic: {
					'eventData.attributes.class':
						'no-follow panel-heading-link panel-toggle'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'navClick',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.text]]',
						position: isMobile() ? 'sidebar' : 'topNav'
					}
				}
			},
			// this is for fragment widget clicks in the main nav megamenu on desktop
			{
				logic: ({ eventData }) => {
					return (
						!['ws-navigation', 'navigation-default'].includes(
							eventData?.widgetName
						) && eventData?.location === 'mainNav'
					);
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: '[[eventData.text]]'
					}
				}
			},
			{
				logic: ({ eventData }) =>
					(eventData?.widgetName === 'model-selector-responsive' &&
						// not ideal but better than attaching to a label
						// also we probably will not be updating this old model selector carousel library
						eventData?.attributes?.class?.includes?.(
							'slick-next'
						)) ||
					eventData?.attributes?.class?.includes?.('slick-prev'),
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: ({ eventData }) =>
							`browse new models ${
								eventData?.attributes?.class?.includes?.('next')
									? 'next'
									: 'previous'
							}`
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'content-default'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'CTA',
					eventAction: 'buttonClick',
					attributes: {
						linkText: '[[eventData.text]]'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'contact-info',
					'eventData.url': '/dealership/directions.htm'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText:
							'[[eventData.text]] >> [[eventData.innerText]]'
					}
				}
			},
			{
				logic: {
					'eventData.widgetName': 'map-dynamic'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'map'
					}
				}
			}
		],
		'ddc.map.pin': [
			{
				logic: {
					'eventData.widgetName': 'map-dynamic'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'map'
					}
				}
			}
		],
		'ddc.link.getDirections': [
			{
				payload: {
					type: 'CustomTagEvent',
					eventName: 'getDirections',
					eventAction: 'pageElementInteraction',
					attributes: {
						linkText: '[[eventData.linkText]]'
					}
				}
			}
		],
		'ddc.message': [
			{
				logic: {
					taggingType: 'stellantis'
				},
				payload: ({
					eventData: {
						// ignore
						element: _element,
						taggingTarget: _taggingTarget,
						taggingType: _taggingType,
						// keep
						...eventData
					} = {}
				}) => ({
					...eventData
				})
			}
		],
		specialsEvent: [
			{
				logic: {
					specialPlacementType: 'tall_horizontal_slide',
					specialType: 'click'
				},
				payload: {
					type: 'CustomTagEvent',
					eventName: 'general',
					eventAction: 'pageElementInteraction',
					attributes: {
						element: 'carousel',
						assetName: '[[eventData.title]]',
						// add 1 to start counting on 1 as the other slideshow
						assetPosition: ({ eventData: { promoIndex } = {} }) =>
							promoIndex + 1
					}
				}
			}
		]
	},
	triggerEvents: {
		telephonicURL: {
			logic: {
				'eventData.isTelephonicUrl': true
			},
			payload: {
				type: 'CustomTagEvent',
				eventName: 'clickToCall',
				eventAction: 'pageElementInteraction',
				attributes: ({ eventData: { department = '' } }) => {
					const CALL_DEPARTMENTS = [
						'Sales',
						'Service',
						'Parts',
						'Other'
					];

					const dept =
						department.charAt(0).toUpperCase() +
						department.slice(1).toLowerCase();
					const idx = CALL_DEPARTMENTS.indexOf(dept);

					return {
						clickToCallDepartment:
							idx === -1 ? 'Other' : CALL_DEPARTMENTS[idx]
					};
				}
			}
		}
	}
};
